import { useState } from 'react'

import { toast } from 'react-toastify'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTrashAlt } from '@fortawesome/pro-light-svg-icons'

import { Box, Button, Link, Search, Text } from '@domain-group/suit-theme'

import type { TemplatePageModel } from '@domain-group/realhub-backend-api-types'

import useDispatch from '@hooks/useDispatch'

import type { EntityOptions } from '@redux/modules/types'
import * as templatePageActions from '@redux/modules/templatePage'

interface CopyFromTemplateProps {
  callbacks: {
    closeModal: () => void,
  },
  selectedTemplateIds: number[]
}

const CopyFromTemplate = (props: CopyFromTemplateProps) => {
  const { callbacks, selectedTemplateIds } = props
  const { closeModal } = callbacks

  const dispatch = useDispatch()

  const [selectedTemplatePage, setSelectedTemplatePage] = useState<TemplatePageModel | null>(null)
  const [copying, setCopying] = useState(false)

  const searchTemplatePages = (string: string, requestOptions: EntityOptions) => (
    dispatch(templatePageActions.loadTemplatePages({ string, ...requestOptions }))
  )

  const copyPermissions = async () => {
    if (!selectedTemplatePage) return

    setCopying(true)

    const result = await dispatch(
      templatePageActions.copyTemplatePagePermissions(selectedTemplatePage.id, selectedTemplateIds),
    )

    setCopying(false)

    if (result.success){
      toast('Permissions Copied Successfully')
      closeModal()
      return
    }

    toast.warn(result.errors.join(', ') || 'Failed to copy permissions')
  }

  return (
    <Box flexDirection="column">
      <Text style={{ marginBottom: '10px' }}>Copy From Template</Text>

      {!selectedTemplatePage && (
        <Search
          callbacks={{
            search: searchTemplatePages,
            selectItem: item => setSelectedTemplatePage(item as unknown as TemplatePageModel),
            mapResults: data => ({
              groups: [
                {
                  key: 'templatePages',
                  title: 'Templates',
                  items: (data as TemplatePageModel[])
                    .filter(templatePage => !selectedTemplateIds.includes(templatePage.id))
                    .map(templatePage => ({
                      ...templatePage,
                      key: templatePage.id,
                      secondaryText: templatePage.builder,
                      text: `${templatePage.title} - #${templatePage.id}`,
                    })),
                },
              ],
            }),
          }}
          endpoint="/template_pages.json"
          placeholder="Search for a template"
        />
      )}

      {selectedTemplatePage && (
        <Box alignItems="center" fontSize="small" height={38} padding="medium" variant="white">
          <Text variant="twoLineEllipsis">
            {`${selectedTemplatePage.title} - #${selectedTemplatePage.id}`}
          </Text>

          <Box marginLeft="auto" width="auto">
            <Link greyLink onClick={() => setSelectedTemplatePage(null)}>
              <FontAwesomeIcon icon={faTrashAlt} />
            </Link>
          </Box>
        </Box>
      )}

      <Button
        buttonStyle="primaryCreate"
        disabled={!selectedTemplatePage || !selectedTemplateIds.length}
        loading={copying}
        marginTop="large"
        onClick={copyPermissions}
        size="medium"
      >
        Copy Permissions
      </Button>
    </Box>
  )
}

export default CopyFromTemplate